import { useEffect, useRef, useState } from 'react';
import type {
  JSX,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { MoveDiagonal } from 'lucide-react';

import { browserStorage } from './devicePrefs';
import type { KeyValueStorage } from './devicePrefs';
import { cn } from '@/lib/utils';
import { VideoTile } from './VideoTile';
import {
  clampPipBox,
  defaultPipBox,
  loadPipLayout,
  movePipBox,
  PIP_KEYBOARD_MOVE_PX,
  PIP_KEYBOARD_RESIZE_PX,
  PIP_PILL_CLEARANCE_PX,
  resizePipBox,
  savePipLayout,
} from './pipLayout';
import type { PipArea, PipBox } from './pipLayout';
import type { ParticipantTile } from './roomState';
import { TILE_ASPECT } from './gridLayout';

export interface SelfPipProps {
  /** The own camera tile (the local participant with a live video track). */
  readonly tile: ParticipantTile;
  /** The measured main area the PiP floats in; re-clamps on every viewport change. */
  readonly area: PipArea;
  /** Horizontally flips the own camera (the "mirror video" option). */
  readonly mirror: boolean;
  /** True while the own participant is an active speaker (the speaking ring). */
  readonly speaking: boolean;
  /** True when the own participant is a room admin (the host shield). */
  readonly isAdmin: boolean;
  /** True while the own hand is raised. */
  readonly handRaised: boolean;
  /** Injectable storage (tests); defaults to the browser localStorage. */
  readonly storage?: KeyValueStorage | null;
}

/** Working pointer-gesture state: the gesture's pointer, its kind and the box it started from. */
interface Gesture {
  readonly pointerId: number;
  readonly kind: 'drag' | 'resize';
  readonly startX: number;
  readonly startY: number;
  readonly base: PipBox;
}

/** jsdom and old engines lack pointer capture; the drag still works through move/up on the element. */
function capturePointer(element: Element, pointerId: number): void {
  if (typeof element.setPointerCapture === 'function') {
    element.setPointerCapture(pointerId);
  }
}

/**
 * BR-DM-5: measures how much of the main area's bottom the fixed mobile control pill covers
 * (the pill band plus a clearance) - the clamp must keep the PiP above it. The pill exists
 * only on the mobile layout (`.fixed.inset-x-0.bottom-0`); desktop returns 0. Measured
 * against the section's live box, so a scrolled or resized stage stays correct.
 */
export function measurePillInset(): number {
  const pill = document.querySelector<HTMLElement>('.fixed.inset-x-0.bottom-0');
  const section = document.querySelector<HTMLElement>('[data-testid="meeting-layout"]');
  if (pill === null || section === null) {
    return 0;
  }
  const band =
    section.getBoundingClientRect().bottom -
    pill.getBoundingClientRect().top +
    PIP_PILL_CLEARANCE_PX;
  return Math.max(0, Math.round(band));
}

/**
 * The floating self-camera tile of the 1:1 layout: wraps the ordinary {@link VideoTile} (avatar,
 * nameplate, speaking-ring semantics unchanged) in a draggable, resizable overlay. Pointer
 * gestures drag the tile anywhere inside the main area and resize it from the corner handle,
 * always clamped by {@link clampPipBox} (fully inside the area, between ~12% and the 30% area
 * cap). The keyboard moves the tile with the arrow keys and resizes it with +/-. The last
 * position and size persist across joins through {@link savePipLayout}.
 */
export function SelfPip(props: SelfPipProps): JSX.Element | null {
  const { t } = useTranslation();
  const { area } = props;
  const storageRef = useRef<KeyValueStorage | null>(
    props.storage === undefined ? browserStorage() : props.storage,
  );
  const [box, setBox] = useState<PipBox | null>(null);
  const boxRef = useRef<PipBox | null>(null);
  const gestureRef = useRef<Gesture | null>(null);
  // The measured pill band (BR-DM-5); refreshed on every area change, read by the gestures.
  const insetRef = useRef(0);

  /** The area as the clamp sees it: the section box minus the pill band. */
  const clampArea = (): PipArea => ({
    width: area.width,
    height: area.height,
    insetBottom: insetRef.current,
  });

  // First valid measurement initializes from the remembered layout (or the default corner);
  // every later viewport change re-clamps the current box into the new area.
  useEffect(() => {
    if (area.width <= 0 || area.height <= 0) {
      return;
    }
    insetRef.current = measurePillInset();
    const clamped = clampArea();
    const storage = storageRef.current;
    const base =
      boxRef.current ??
      (storage !== null ? loadPipLayout(storage, clamped) : null) ??
      defaultPipBox(clamped);
    const next = clampPipBox(base, clamped);
    boxRef.current = next;
    setBox(next);
  }, [area.width, area.height]);

  const apply = (next: PipBox): void => {
    boxRef.current = next;
    setBox(next);
  };

  const persist = (): void => {
    const storage = storageRef.current;
    const current = boxRef.current;
    if (storage !== null && current !== null) {
      savePipLayout(storage, current, clampArea());
    }
  };

  /**
   * Begins the gesture bound to the element the handler sits on: drags start on the tile surface
   * but never on a button; the resize gesture starts on the handle button itself. Pointer capture
   * keeps the move/up stream flowing when the pointer leaves the element mid-gesture.
   */
  const startGesture = (event: ReactPointerEvent<Element>, kind: 'drag' | 'resize'): void => {
    if (!event.isPrimary || boxRef.current === null) {
      return;
    }
    const startedOnButton =
      event.target instanceof Element && event.target.closest('button') !== null;
    if ((kind === 'drag' && startedOnButton) || (kind === 'resize' && !startedOnButton)) {
      return;
    }
    capturePointer(event.currentTarget, event.pointerId);
    gestureRef.current = {
      pointerId: event.pointerId,
      kind,
      startX: event.clientX,
      startY: event.clientY,
      base: boxRef.current,
    };
  };

  const moveDrag = (event: ReactPointerEvent<Element>): void => {
    const gesture = gestureRef.current;
    if (gesture === null || gesture.kind !== 'drag' || gesture.pointerId !== event.pointerId) {
      return;
    }
    apply(
      movePipBox(
        gesture.base,
        event.clientX - gesture.startX,
        event.clientY - gesture.startY,
        clampArea(),
      ),
    );
  };

  const moveResize = (event: ReactPointerEvent<Element>): void => {
    const gesture = gestureRef.current;
    if (gesture === null || gesture.kind !== 'resize' || gesture.pointerId !== event.pointerId) {
      return;
    }
    const dx = event.clientX - gesture.startX;
    const dy = event.clientY - gesture.startY;
    // Either axis grows the tile; the width maps the vertical delta through the 16:9 aspect.
    const targetWidth = gesture.base.width + Math.max(dx, dy * TILE_ASPECT);
    apply(resizePipBox(gesture.base, targetWidth, clampArea()));
  };

  const endGesture = (event: ReactPointerEvent<Element>): void => {
    const gesture = gestureRef.current;
    if (gesture === null || gesture.pointerId !== event.pointerId) {
      return;
    }
    gestureRef.current = null;
    persist();
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const step = event.shiftKey ? PIP_KEYBOARD_MOVE_PX * 4 : PIP_KEYBOARD_MOVE_PX;
    const current = boxRef.current;
    if (current === null) {
      return;
    }
    const clamped = clampArea();
    switch (event.key) {
      case 'ArrowLeft':
        apply(movePipBox(current, -step, 0, clamped));
        break;
      case 'ArrowRight':
        apply(movePipBox(current, step, 0, clamped));
        break;
      case 'ArrowUp':
        apply(movePipBox(current, 0, -step, clamped));
        break;
      case 'ArrowDown':
        apply(movePipBox(current, 0, step, clamped));
        break;
      case '+':
      case '=':
        apply(resizePipBox(current, current.width + PIP_KEYBOARD_RESIZE_PX, clamped));
        break;
      case '-':
      case '_':
        apply(resizePipBox(current, current.width - PIP_KEYBOARD_RESIZE_PX, clamped));
        break;
      default:
        return;
    }
    event.preventDefault();
    persist();
  };

  if (box === null) {
    return null;
  }
  return (
    <div
      data-testid="pip"
      role="group"
      aria-label={t('room.pip_aria')}
      tabIndex={0}
      className="absolute z-20 cursor-grab touch-none select-none rounded-xl shadow-2xl outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/80 active:cursor-grabbing"
      style={{ left: box.x, top: box.y, width: box.width, height: box.height }}
      onKeyDown={onKeyDown}
      onPointerDown={(event) => {
        startGesture(event, 'drag');
      }}
      onPointerMove={moveDrag}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
    >
      <VideoTile
        tile={props.tile}
        fill
        speaking={props.speaking}
        isAdmin={props.isAdmin}
        handRaised={props.handRaised}
        mirror={props.mirror}
      />
      <button
        type="button"
        data-testid="pip-handle"
        aria-label={t('room.pip_resize_aria')}
        className={cn(
          'absolute right-0.5 bottom-0.5 z-30 grid size-7 cursor-nwse-resize touch-none place-items-center',
          'rounded-md bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/70',
          'outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/80',
        )}
        onPointerDown={(event) => {
          startGesture(event, 'resize');
        }}
        onPointerMove={moveResize}
        onPointerUp={endGesture}
        onPointerCancel={endGesture}
      >
        <MoveDiagonal className="size-3" aria-hidden />
      </button>
    </div>
  );
}
